// "Based on your sales" — the one thing this sort order needs on the client.
//
// Picking it can require a ~3.5 s build (see todo/sales-based-recommendations.md): three
// seed queries, an embedding fetch, k-means, and one vector search per cluster. That is far
// too long to leave the page looking idle, and a reader who clicks something else mid-build
// abandons the request — so the page is locked while it runs, exactly as a browser download
// locks it.
//
// Every subsequent visit skips all of this: the mutation returns `built: false` for a list
// that is already fresh, which costs one primary-key read.
import { api } from "../api";
import { toastError } from "../ui/messages";

/** The filter option id, shared with `views/data/filters.ts`. */
export const SALES_SORT_OPTION = "sort-my-sales";

const LOCK_ID = "salesrec-lock";

/**
 * How long to wait before showing the lock.
 *
 * Only the FIRST activation for a reseller does real work (~3.5 s, or ~7 s when the shared
 * tier-3 list has to be built too). Every later one is a primary-key read on
 * `ResellerRecommendationBuild` that returns `built: false` in tens of milliseconds — and
 * showing a full-screen "Building recommendations…" panel for that long reads as a glitch,
 * not as progress. 500 ms is comfortably longer than any cache hit and a small fraction of
 * a real build, so the lock appears only when there is genuinely something to wait for.
 */
const LOCK_DELAY_MS = 500;

/**
 * The full-screen lock, built with `createElement` rather than an HTML string.
 *
 * Browse's sidebar is server-rendered with no Preact root to mount into here, and a string
 * of markup is the form that has repeatedly smuggled dead inline handlers into this codebase
 * (see the `check:inline-js` notes in CLAUDE.md). There is nothing interactive in this
 * element, which is the point — while it is up, Stop is not even an option, because the
 * build is a single request that either finishes or does not.
 */
function showLock(): void {
  if (document.getElementById(LOCK_ID)) return;
  const lock = document.createElement("div");
  lock.className = "browserdl-lock";
  lock.id = LOCK_ID;

  const box = document.createElement("div");
  box.className = "browserdl-lock-box";

  // Spinner on its own row above the title, not inline with it: this panel is up for
  // several seconds with nothing else on it, so the spinner is the only thing saying "still
  // working" and should be seen rather than read past. Sized and spaced by
  // `.salesrec-spinner` in styles/features/incoming.css.
  const spinnerRow = document.createElement("div");
  spinnerRow.className = "salesrec-spinner";
  const spinner = document.createElement("i");
  spinner.className = "fas fa-spinner fa-spin";
  spinnerRow.append(spinner);

  const title = document.createElement("div");
  title.className = "browserdl-title";
  title.textContent = "Building recommendations based on your sales data";

  const sub = document.createElement("div");
  sub.className = "browserdl-sub";
  sub.textContent = "This runs once. Next time it loads instantly.";

  box.append(spinnerRow, title, sub);
  lock.append(box);
  document.body.append(lock);
}

function hideLock(): void {
  document.getElementById(LOCK_ID)?.remove();
}

/**
 * Make sure the reseller has a candidate list, building it behind the lock if not.
 *
 * Resolves `true` when the grid can be loaded with this sort. On a failure it resolves
 * `false` — the caller then leaves the previous sort in place rather than reloading into an
 * empty grid, because `search.load` deliberately falls back to the Recommended order when
 * nothing is built (`load/salesList.ts`) and a silent fallback would read as the option
 * doing nothing.
 */
export async function ensureSalesRecommendations(): Promise<boolean> {
  // Armed rather than shown: see LOCK_DELAY_MS. The timer is cleared in `finally`, so a
  // fast reply never paints the lock at all.
  const armed = setTimeout(showLock, LOCK_DELAY_MS);
  try {
    const res = await api.search.buildSalesRecommendations({ force: false });
    // "building" means another tab or request claimed it. Its list will be there shortly;
    // loading the grid now would show the previous order, so say so instead of lying.
    if (res.status === "building") {
      toastError("Recommendations are still being built — try again in a moment");
      return false;
    }
    return true;
  } catch (err: unknown) {
    toastError(err instanceof Error ? err.message : "Could not build your recommendations");
    return false;
  } finally {
    clearTimeout(armed);
    hideLock();
  }
}
